/**
 * Closing readiness + pipeline-specific scheduling — wraps the readiness half
 * of the `/closing/*` router (eastview-os frontend_documentation/
 * closing-readiness.md, 2026-09-14). The OOO / bank-holiday / timezone half
 * lives in closingCoverageApi.js.
 *
 * One deterministic per-loan signal serves two audiences: closers read it as
 * Clear-To-Close readiness, Capital Markets reads it as "ready to fund?".
 * Every call here is pipeline-aware — `pipeline` is `preclose` (a closing) or
 * `acquisition` (a trade settlement); the two are never rendered with one
 * vocabulary (see closingReadinessUtils.modeLabel).
 */
import { losHttp } from "../lib/losHttp";

const cfg = (logName) => ({ logName });

// Query-string helper: drops undefined / null / "" so `pipeline=all` and an
// unset pipeline serialize the same way the backend expects.
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );

export const READINESS_PIPELINES = {
  PRECLOSE: "preclose",
  ACQUISITION: "acquisition",
  ALL: "all",
};

export const closingReadinessApi = {
  // GET /closing/schedule?start&end&pipeline → { items[cards], summary }.
  // Every loan whose FUNDING date falls in the window, as truncated cards. This
  // replaces the client-side "Clear To Close" slice of the pipeline list the
  // calendars used to build their chips from.
  getSchedule: ({ start, end, pipeline = READINESS_PIPELINES.ALL } = {}) =>
    losHttp
      .get("/closing/schedule", {
        params: clean({ start, end, pipeline }),
        ...cfg("Closing schedule feed"),
      })
      .then((r) => r.data),

  // GET /closing/readiness/{dealId}[?pipeline] → the full readiness object
  // (card fields + scheduling / documents / conditions / exceptions /
  // gatingItems / lock / closingDocuments / aiReview blocks).
  getReadiness: (dealId, { pipeline } = {}) =>
    losHttp
      .get(`/closing/readiness/${dealId}`, {
        params: clean({ pipeline }),
        ...cfg("Closing readiness"),
      })
      .then((r) => r.data),

  // GET /closing/readiness/{dealId}/ai-review → the stored closing_readiness
  // agent review in full, or 404 when it has never run.
  getAiReview: (dealId, { pipeline } = {}) =>
    losHttp
      .get(`/closing/readiness/${dealId}/ai-review`, {
        params: clean({ pipeline }),
        ...cfg("Closing readiness AI review"),
      })
      .then((r) => r.data),

  // GET /closing/readiness/{dealId}/closing-docs → the closing-documents
  // requirement rows + `uploadDocTypes` (the docTypes to upload each with).
  getClosingDocs: (dealId, { pipeline } = {}) =>
    losHttp
      .get(`/closing/readiness/${dealId}/closing-docs`, {
        params: clean({ pipeline }),
        ...cfg("Closing documents"),
      })
      .then((r) => r.data),

  // POST /closing/readiness/{dealId}/closing-docs/compare → runs the
  // deal-vs-Note-vs-HUD tie-out over the extracts, stages divergences as
  // findings, returns { ran, findingsStaged, closingDocuments }.
  compareClosingDocs: (dealId, { pipeline } = {}) =>
    losHttp
      .post(
        `/closing/readiness/${dealId}/closing-docs/compare`,
        {},
        { params: clean({ pipeline }), ...cfg("Compare closing docs to terms") },
      )
      .then((r) => r.data),

  // PUT /closing/schedule/{dealId} — pipeline-specific date/status writes
  // directly on the loan. The pipeline decides the allowed body; anything else
  // is 400 with `allowed[]`. Returns { dealId, pipeline, updated{}, readiness{} }.
  // NOT for Closed / Settled — those stay on the Mark Closed / Mark Settled modals.
  updateSchedule: (dealId, body, { pipeline } = {}) =>
    losHttp
      .put(`/closing/schedule/${dealId}`, body, {
        params: clean({ pipeline }),
        ...cfg("Update closing schedule"),
      })
      .then((r) => r.data),

  // GET /closing/funding-outlook?start&end&pipeline → expected fundings by
  // date: { days[{date, count, amount, readyCount, readyAmount, closings,
  // trades, wireBlocked, items[]}], totals{} }. Closed loans excluded.
  getFundingOutlook: ({ start, end, pipeline = READINESS_PIPELINES.ALL } = {}) =>
    losHttp
      .get("/closing/funding-outlook", {
        params: clean({ start, end, pipeline }),
        ...cfg("Funding outlook"),
      })
      .then((r) => r.data),
};

export default closingReadinessApi;
